import { useQuery } from '@tanstack/react-query';
import type { PushConfigResponse, PushDeviceDto, PushTestResponse } from '@kominiarz/shared';
import { api } from './api';

/**
 * Web Push on this device. Permission is only ever requested from a button the user
 * taps (iOS requires it, and a prompt on start-up is rude anyway).
 */

export const PUSH_KEY = ['push'] as const;

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iPhone/iPad: push exists only in the app added to the home screen (iOS 16.4+). */
export function iosNeedsInstall(): boolean {
  return isIos() && !isStandalone();
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function sameKey(sub: PushSubscription, publicKey: string): boolean {
  const current = sub.options.applicationServerKey;
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = keyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Service worker not ready')), 10_000));
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    return await (await registration()).pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Asks for permission (after a tap) and registers this device with the server. */
export async function enablePush(publicKey: string): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, publicKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
  await api('/push/subscriptions', { method: 'POST', body: sub.toJSON() });
  return permission;
}

/** "Wyłącz na tym urządzeniu". */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api('/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } });
  await sub.unsubscribe();
}

/** On sign-out: the next person on this device has to switch notifications on themselves. */
export async function forgetPushOnDevice(): Promise<void> {
  const sub = await currentSubscription();
  await sub?.unsubscribe().catch(() => undefined);
}

/**
 * On start-up: re-register an existing subscription (it is tied to the session on the
 * server) and renew it if the server's key changed. Never asks for permission.
 */
export async function syncPushSubscription(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  const sub = await currentSubscription();
  if (!sub) return;
  const config = await api<PushConfigResponse>('/push/config');
  if (!config.enabled || !config.publicKey) return;
  if (sameKey(sub, config.publicKey)) {
    await api('/push/subscriptions', { method: 'POST', body: sub.toJSON() });
  } else {
    await sub.unsubscribe();
    await enablePush(config.publicKey);
  }
}

export type PushStatus =
  | 'unsupported'
  | 'ios-needs-install'
  | 'server-off'
  | 'denied'
  | 'off'
  | 'on';

/** Everything the settings screen needs to know about notifications on this device. */
export function usePushStatus() {
  return useQuery({
    queryKey: [...PUSH_KEY, 'status'],
    queryFn: async (): Promise<{ status: PushStatus; publicKey: string | null }> => {
      if (iosNeedsInstall()) return { status: 'ios-needs-install', publicKey: null };
      if (!pushSupported()) return { status: 'unsupported', publicKey: null };
      const config = await api<PushConfigResponse>('/push/config');
      if (!config.enabled || !config.publicKey) return { status: 'server-off', publicKey: null };
      if (Notification.permission === 'denied') return { status: 'denied', publicKey: config.publicKey };
      const sub = Notification.permission === 'granted' ? await currentSubscription() : null;
      return { status: sub ? 'on' : 'off', publicKey: config.publicKey };
    },
    staleTime: 60_000,
  });
}

export function usePushDevices(enabled: boolean) {
  return useQuery({
    queryKey: [...PUSH_KEY, 'devices'],
    queryFn: async () => (await api<{ devices: PushDeviceDto[] }>('/push/subscriptions')).devices,
    enabled,
  });
}

export const sendTestPush = () => api<PushTestResponse>('/push/test', { method: 'POST' });
export const removePushDevice = (id: number) => api<{ ok: true }>(`/push/subscriptions/${id}`, { method: 'DELETE' });
